import { useState } from "react";
import {
  PageShell,
  PageHeader,
  ListView,
  ListSkeleton,
  EmptyState,
  FilterTabs,
} from "../../../Components/shared";
import { FaThLarge, FaPlus, FaBoxOpen, FaCoffee, FaTags } from "react-icons/fa";
import { Modal } from "../../../Components/shared";
import useAuth from "../../../Hooks/useAuth";
import { useAlert } from "../../../context/AlertContext";
import {
  useGetFactoryFullCatalogQuery,
  useCreateFactoryPartMutation,
  useUpdateFactoryPartMutation,
  useCreateFactoryMachineTypeMutation,
  useUpdateFactoryMachineTypeMutation,
  useUpdateFactoryBomMutation,
  useCreateFactoryLabelMutation,
  useUpdateFactoryLabelMutation,
  useDeleteFactoryLabelMutation,
  useDeleteFactoryPartMutation,
  useUploadFactoryPartImageMutation,
  useDeleteFactoryPartImageMutation,
  useUploadFactoryMachineTypeImageMutation,
  useDeleteFactoryMachineTypeImageMutation,
} from "../../../store";
import CatalogThumb from "../../../Components/factory/storageRoles/CatalogThumb";
import RowActionButton from "../../../Components/factory/storageRoles/RowActionButton";
import PartTypeModal from "../../../Components/factory/storageRoles/PartTypeModal";
import MachineTypeModal from "../../../Components/factory/storageRoles/MachineTypeModal";

const TABS = [
  { id: "parts", label: "Parça Tipleri", icon: FaBoxOpen },
  { id: "machines", label: "Makine Tipleri", icon: FaCoffee },
  { id: "families", label: "Ürün Aileleri", icon: FaTags },
];

const PART_HEADERS = [
  { label: "PARÇA", className: "w-[20%]" },
  { label: "KOD", className: "w-[10%]" },
  { label: "ÜRÜN AİLELERİ", className: "w-[20%]" },
  { label: "ALT PARÇALAR", className: "w-[20%]" },
  { label: "ETİKET", className: "w-[10%]" },
  { label: "DURUM", className: "w-[10%]" },
  { label: "İŞLEM", className: "w-[10%] text-right" },
];

/** Parça tipinin üzerindeki etiket türleri (QR kod / barkod) — yoksa "-". */
const labelKindsOf = (p) =>
  [p.hasQrCode && "QR", p.hasBarcode && "Barkod"].filter(Boolean).join(", ") || "-";

const MACHINE_HEADERS = [
  { label: "MAKİNE TİPİ", className: "w-[26%]" },
  { label: "KOD", className: "w-[10%]" },
  { label: "KÖKEN", className: "w-[12%]" },
  { label: "GÖZ", className: "w-[8%]" },
  { label: "BOM SLOTU", className: "w-[32%]" },
  { label: "DURUM", className: "w-[12%]" },
];

const FAMILY_HEADERS = [
  { label: "ÜRÜN AİLESİ", className: "w-[34%]" },
  { label: "PARÇA SAYISI", className: "w-[18%]" },
  { label: "DURUM", className: "w-[18%]" },
  { label: "İŞLEM", className: "w-[30%] text-right" },
];

const nameOf = (ref) => ref?.name || "";

/**
 * Fabrika Kataloğu (D-058) — parça/makine tipleri panelden ELLE yönetilir.
 * Seed yok; fiziksel üretim/montaj tabletten yapılır, burası yalnız tanımlar.
 */
export default function FactoryCatalogPage() {
  const { auth } = useAuth();
  const token = auth?.accessToken;
  const { showSuccess, showError } = useAlert();

  const [tab, setTab] = useState("parts");
  // editing: { kind: "part"|"machine", item: obj|null } | null
  const [editing, setEditing] = useState(null);

  const { data, isLoading, refetch } = useGetFactoryFullCatalogQuery(
    { token },
    { skip: !token }
  );
  const parts = data?.parts || [];
  const machineTypes = data?.machineTypes || [];
  const families = data?.families || [];

  const [createPart, { isLoading: cp }] = useCreateFactoryPartMutation();
  const [updatePart, { isLoading: up }] = useUpdateFactoryPartMutation();
  const [createMachineType, { isLoading: cm }] = useCreateFactoryMachineTypeMutation();
  const [updateMachineType, { isLoading: um }] = useUpdateFactoryMachineTypeMutation();
  const [updateBom, { isLoading: ub }] = useUpdateFactoryBomMutation();
  const [createLabel] = useCreateFactoryLabelMutation();
  const [updateLabel] = useUpdateFactoryLabelMutation();
  const [deleteLabel] = useDeleteFactoryLabelMutation();
  const [deletePart] = useDeleteFactoryPartMutation();
  const [uploadPartImage, { isLoading: upi }] = useUploadFactoryPartImageMutation();
  const [deletePartImage, { isLoading: dpi }] = useDeleteFactoryPartImageMutation();
  const [uploadMachineImage, { isLoading: umi }] =
    useUploadFactoryMachineTypeImageMutation();
  const [deleteMachineImage, { isLoading: dmi }] =
    useDeleteFactoryMachineTypeImageMutation();

  /** Silme onayı: { kind: "part"|"family", item } */
  const [deleteTarget, setDeleteTarget] = useState(null);

  const submitDelete = async () => {
    const { kind, item } = deleteTarget;
    try {
      if (kind === "part") {
        await deletePart({ token, id: item._id }).unwrap();
        showSuccess("Parça tipi silindi");
      } else {
        await deleteLabel({ token, id: item._id }).unwrap();
        showSuccess("Ürün ailesi silindi");
      }
      setDeleteTarget(null);
      refetch();
    } catch (err) {
      setDeleteTarget(null);
      showError(err?.data?.message || "Silinemedi");
    }
  };
  const saving = cp || up || cm || um || ub || upi || dpi || umi || dmi;

  /**
   * Görsel niyetini kayıt sonrasında uygular — multipart ayrı endpoint'e gider
   * ve yeni kayıtta id ancak create döndükten sonra bilinir.
   *
   * Görsel hatası kaydı geçersiz kılmaz (kayıt durur), ama false döner: tek
   * toast slotu olduğu için çağıran başarı mesajını bastırmalı, yoksa hata
   * mesajı anında ezilir ve kullanıcı yüklemenin başarısız olduğunu görmez.
   *
   * @returns {Promise<boolean>} false = görsel adımı hata verdi (toast gösterildi)
   */
  const applyImageIntent = async (form, id, { upload, remove }) => {
    if (!id) return true;
    try {
      if (form.imageFile) {
        await upload({ token, id, file: form.imageFile }).unwrap();
      } else if (form.imageCleared) {
        await remove({ token, id }).unwrap();
      }
      return true;
    } catch (err) {
      showError(
        err?.data?.message || "Kayıt kaydedildi ancak görsel yüklenemedi"
      );
      return false;
    }
  };

  // Ürün ailesi ekle/düzenle — kendi sekmesinden yönetilir (parça formundan değil)
  const [familyDraft, setFamilyDraft] = useState(null); // {family?, name}

  const submitFamily = async () => {
    const name = (familyDraft?.name || "").trim();
    if (!name) return;
    try {
      if (familyDraft.family) {
        await updateLabel({ token, id: familyDraft.family._id, name }).unwrap();
        showSuccess("Ürün ailesi güncellendi");
      } else {
        await createLabel({ token, kind: "family", name }).unwrap();
        showSuccess("Ürün ailesi eklendi");
      }
      setFamilyDraft(null);
      refetch();
    } catch (err) {
      showError(err?.data?.message || "Kaydedilemedi");
    }
  };

  const toggleFamilyActive = async (family) => {
    try {
      await updateLabel({ token, id: family._id, isActive: !family.isActive }).unwrap();
      showSuccess(family.isActive ? "Ürün ailesi pasife alındı" : "Ürün ailesi aktifleştirildi");
      refetch();
    } catch (err) {
      showError(err?.data?.message || "İşlem başarısız");
    }
  };

  /** Aile başına kaç parça etiketli (silme yerine pasife alma kararında yardımcı) */
  const partCountByFamily = (familyId) =>
    parts.filter((p) => (p.families || []).some((f) => String(f._id || f) === String(familyId)))
      .length;

  const submitPart = async (form) => {
    // Görsel alanları JSON gövdeye girmez — ayrı multipart endpoint'ten gider
    const { image, imageFile, imageCleared, ...fields } = form;
    try {
      let id = editing?.item?._id;
      if (id) {
        await updatePart({ token, id, ...fields }).unwrap();
      } else {
        const res = await createPart({ token, ...fields }).unwrap();
        id = res?.data?.part?._id;
      }
      const imageOk = await applyImageIntent(form, id, {
        upload: uploadPartImage,
        remove: deletePartImage,
      });
      if (imageOk) {
        showSuccess(
          editing?.item ? "Parça tipi güncellendi" : "Parça tipi oluşturuldu"
        );
      }
      setEditing(null);
      refetch();
    } catch (err) {
      showError(err?.data?.message || "Kaydedilemedi");
    }
  };

  const submitMachine = async (form) => {
    // Görsel alanları JSON gövdeye girmez — ayrı multipart endpoint'ten gider
    const { slots, image, imageFile, imageCleared, ...typeFields } = form;
    try {
      let id = editing?.item?._id;
      if (id) {
        await updateMachineType({ token, id, ...typeFields }).unwrap();
      } else {
        const res = await createMachineType({ token, ...typeFields }).unwrap();
        id = res?.data?.machineType?._id;
      }
      if (id && typeFields.origin === "produced") {
        await updateBom({ token, id, slots }).unwrap();
      }
      const imageOk = await applyImageIntent(form, id, {
        upload: uploadMachineImage,
        remove: deleteMachineImage,
      });
      if (imageOk) {
        showSuccess(
          editing?.item ? "Makine tipi güncellendi" : "Makine tipi oluşturuldu"
        );
      }
      setEditing(null);
      refetch();
    } catch (err) {
      showError(err?.data?.message || "Kaydedilemedi");
    }
  };

  const activeBadge = (isActive) => (
    <span
      className={`inline-flex px-2 py-0.5 rounded-full text-xs font-semibold border ${
        isActive
          ? "bg-emerald-50 text-emerald-700 border-emerald-100"
          : "bg-gray-50 text-gray-500 border-gray-100"
      }`}
    >
      {isActive ? "Aktif" : "Pasif"}
    </span>
  );

  return (
    <PageShell>
      <div className="px-3 py-2.5 sm:p-4 md:p-6 border-b border-gray-100 shrink-0">
        <PageHeader
          icon={FaThLarge}
          iconColor="purple"
          title="Fabrika Kataloğu"
          subtitle="Parça ve makine tipi şablonları — üretim tabletten yapılır"
          stats={[
            { label: "Parça Tipi", value: parts.length },
            { label: "Makine Tipi", value: machineTypes.length },
          ]}
          isLoading={isLoading}
          actions={[
            {
              label:
                tab === "parts"
                  ? "Yeni Parça Tipi"
                  : tab === "machines"
                    ? "Yeni Makine Tipi"
                    : "Yeni Ürün Ailesi",
              icon: FaPlus,
              variant: "primary",
              onClick: () => {
                if (tab === "families") setFamilyDraft({ name: "" });
                else setEditing({ kind: tab === "parts" ? "part" : "machine", item: null });
              },
            },
          ]}
        />
        <div className="mt-3">
          <FilterTabs
            filters={TABS}
            activeFilter={tab}
            onChange={setTab}
            color="purple"
          />
        </div>
      </div>

      {tab === "parts" && (
        <ListView
          headers={PART_HEADERS}
          tableClassName="table-fixed"
          data={parts}
          isLoading={isLoading}
          loadingNode={
            <ListSkeleton
              columns={PART_HEADERS.map((h) => ({ label: h.label }))}
              rowCount={8}
              message="Katalog yükleniyor..."
            />
          }
          empty={
            <EmptyState
              message="Henüz parça tipi tanımlanmadı"
              suggestion='"Yeni Parça Tipi" ile ilk tanımı ekleyin — tablet üretim listesi buradan beslenir'
            />
          }
          renderRow={(p) => (
            <tr
              key={p._id}
              onClick={() => setEditing({ kind: "part", item: p })}
              className="border-b border-gray-100 hover:bg-gray-50 cursor-pointer"
            >
              <td className="px-4 py-3 text-sm font-semibold text-gray-900 truncate">
                <span className="flex items-center gap-2 min-w-0">
                  <CatalogThumb src={p.image} alt={p.name} />
                  <span className="truncate">{p.name}</span>
                </span>
              </td>
              <td className="px-4 py-3 font-mono text-sm text-gray-600">{p.code}</td>
              <td className="px-4 py-3 text-sm text-gray-600 truncate">
                {(p.families || []).map(nameOf).filter(Boolean).join(", ") || "-"}
              </td>
              <td className="px-4 py-3 text-sm text-gray-500 truncate">
                {(p.subParts || []).map((sp) => sp.name).join(", ") || "-"}
              </td>
              <td className="px-4 py-3 text-sm text-gray-500">{labelKindsOf(p)}</td>
              <td className="px-4 py-3">{activeBadge(p.isActive)}</td>
              <td className="px-4 py-3">
                <div className="flex justify-end">
                  <RowActionButton
                    variant="danger"
                    onClick={(e) => {
                      e.stopPropagation();
                      setDeleteTarget({ kind: "part", item: p });
                    }}
                  >
                    Sil
                  </RowActionButton>
                </div>
              </td>
            </tr>
          )}
          renderMobileCard={(p) => (
            <div
              key={p._id}
              onClick={() => setEditing({ kind: "part", item: p })}
              className="bg-white border border-gray-200 rounded-xl p-3 flex flex-col gap-1"
            >
              <div className="flex items-center justify-between gap-2">
                <span className="flex items-center gap-2 min-w-0">
                  <CatalogThumb src={p.image} alt={p.name} />
                  <span className="text-sm font-semibold text-gray-900 truncate">{p.name}</span>
                </span>
                <span className="font-mono text-xs text-gray-400 shrink-0">{p.code}</span>
              </div>
              <div className="text-xs text-gray-500">
                {(p.families || []).map(nameOf).filter(Boolean).join(", ") || "Ailesiz"} · Etiket:{" "}
                {labelKindsOf(p)}
              </div>
            </div>
          )}
        />
      )}

      {tab === "machines" && (
        <ListView
          headers={MACHINE_HEADERS}
          tableClassName="table-fixed"
          data={machineTypes}
          isLoading={isLoading}
          loadingNode={
            <ListSkeleton
              columns={MACHINE_HEADERS.map((h) => ({ label: h.label }))}
              rowCount={6}
              message="Katalog yükleniyor..."
            />
          }
          empty={
            <EmptyState
              message="Henüz makine tipi tanımlanmadı"
              suggestion='"Yeni Makine Tipi" ile ekleyin; BOM slotlarını da aynı formda seçersiniz'
            />
          }
          renderRow={(mt) => (
            <tr
              key={mt._id}
              onClick={() => setEditing({ kind: "machine", item: mt })}
              className="border-b border-gray-100 hover:bg-gray-50 cursor-pointer"
            >
              <td className="px-4 py-3 text-sm font-semibold text-gray-900 truncate">
                <span className="flex items-center gap-2 min-w-0">
                  <CatalogThumb src={mt.image} alt={mt.name} />
                  <span className="truncate">{mt.name}</span>
                </span>
              </td>
              <td className="px-4 py-3 font-mono text-sm text-gray-600">{mt.code}</td>
              <td className="px-4 py-3 text-sm text-gray-600">
                {mt.origin === "imported" ? "İthal" : "Üretim"}
              </td>
              <td className="px-4 py-3 text-sm text-gray-600">{mt.cupNumber}</td>
              <td className="px-4 py-3 text-sm text-gray-500 truncate">
                {(mt.bomSlots || []).length > 0
                  ? (mt.bomSlots || [])
                      .map((s) => `${s.part?.name}${s.optional ? " (ops.)" : ""}`)
                      .join(", ")
                  : "-"}
              </td>
              <td className="px-4 py-3">{activeBadge(mt.isActive)}</td>
            </tr>
          )}
          renderMobileCard={(mt) => (
            <div
              key={mt._id}
              onClick={() => setEditing({ kind: "machine", item: mt })}
              className="bg-white border border-gray-200 rounded-xl p-3 flex flex-col gap-1"
            >
              <div className="flex items-center justify-between gap-2">
                <span className="flex items-center gap-2 min-w-0">
                  <CatalogThumb src={mt.image} alt={mt.name} />
                  <span className="text-sm font-semibold text-gray-900 truncate">{mt.name}</span>
                </span>
                <span className="font-mono text-xs text-gray-400 shrink-0">{mt.code}</span>
              </div>
              <div className="text-xs text-gray-500">
                {mt.origin === "imported" ? "İthal" : "Üretim"} · {mt.cupNumber} göz ·{" "}
                {(mt.bomSlots || []).length} slot
              </div>
            </div>
          )}
        />
      )}

      {tab === "families" && (
        <ListView
          headers={FAMILY_HEADERS}
          tableClassName="table-fixed"
          data={families}
          isLoading={isLoading}
          loadingNode={
            <ListSkeleton
              columns={FAMILY_HEADERS.map((h) => ({ label: h.label }))}
              rowCount={6}
              message="Ürün aileleri yükleniyor..."
            />
          }
          empty={
            <EmptyState
              message="Henüz ürün ailesi yok"
              suggestion='"Yeni Ürün Ailesi" ile ekleyin (Çayset, Fincanda…) — parça tipi formunda buradan seçilir'
            />
          }
          renderRow={(f) => (
            <tr key={f._id} className="border-b border-gray-100 hover:bg-gray-50">
              <td className="px-4 py-3 text-sm font-semibold text-gray-900">{f.name}</td>
              <td className="px-4 py-3 text-sm text-gray-600">{partCountByFamily(f._id)}</td>
              <td className="px-4 py-3">{activeBadge(f.isActive)}</td>
              <td className="px-4 py-3">
                <div className="flex items-center justify-end gap-1.5">
                  <RowActionButton
                    variant="primary"
                    onClick={() => setFamilyDraft({ family: f, name: f.name })}
                  >
                    Adını Değiştir
                  </RowActionButton>
                  <RowActionButton
                    variant={f.isActive ? "warning" : "success"}
                    onClick={() => toggleFamilyActive(f)}
                  >
                    {f.isActive ? "Pasife Al" : "Aktifleştir"}
                  </RowActionButton>
                  <RowActionButton
                    variant="danger"
                    onClick={() => setDeleteTarget({ kind: "family", item: f })}
                  >
                    Sil
                  </RowActionButton>
                </div>
              </td>
            </tr>
          )}
          renderMobileCard={(f) => (
            <div
              key={f._id}
              className="bg-white border border-gray-200 rounded-xl p-3 flex items-center justify-between"
            >
              <div>
                <div className="text-sm font-semibold text-gray-900">{f.name}</div>
                <div className="text-xs text-gray-500">{partCountByFamily(f._id)} parça</div>
              </div>
              <button
                onClick={() => setFamilyDraft({ family: f, name: f.name })}
                className="text-xs font-semibold text-indigo-600"
              >
                Düzenle
              </button>
            </div>
          )}
        />
      )}

      {/* Silme onayı — sunucu kullanımdaysa 422 ile reddeder */}
      <Modal open={Boolean(deleteTarget)} onClose={() => setDeleteTarget(null)} maxWidth="md">
        <Modal.Header
          title={deleteTarget?.kind === "part" ? "Parça Tipini Sil" : "Ürün Ailesini Sil"}
          subtitle={deleteTarget?.item?.name}
          onClose={() => setDeleteTarget(null)}
        />
        <Modal.Body>
          <p className="text-sm text-gray-600">
            {deleteTarget?.kind === "part"
              ? "Bu tanım kalıcı olarak silinecek ve bulunduğu makine BOM'larından çıkarılacak. Bu tipten üretilmiş parça varsa silme reddedilir — o durumda 'Pasife Al' kullanın."
              : "Bu ürün ailesi kalıcı olarak silinecek. Herhangi bir parça tipi bu aileye bağlıysa silme reddedilir — o durumda 'Pasife Al' kullanın."}
          </p>
        </Modal.Body>
        <Modal.Footer>
          <button
            onClick={() => setDeleteTarget(null)}
            className="px-3.5 py-2 text-sm font-semibold text-gray-600 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
          >
            Vazgeç
          </button>
          <button
            onClick={submitDelete}
            className="px-3.5 py-2 text-sm font-semibold text-white bg-fifth rounded-lg hover:opacity-90"
          >
            Kalıcı Olarak Sil
          </button>
        </Modal.Footer>
      </Modal>

      {/* Ürün ailesi ekle / adını değiştir */}
      <Modal open={Boolean(familyDraft)} onClose={() => setFamilyDraft(null)} maxWidth="md">
        <Modal.Header
          title={familyDraft?.family ? "Ürün Ailesini Düzenle" : "Yeni Ürün Ailesi"}
          subtitle="Parça tipleri bu ailelere etiketlenir; tablette üretim menüsü buna göre gruplanır"
          onClose={() => setFamilyDraft(null)}
        />
        <Modal.Body>
          <label className="block text-xs font-semibold text-gray-500 mb-1">Ad</label>
          <input
            type="text"
            value={familyDraft?.name || ""}
            onChange={(e) => setFamilyDraft((d) => ({ ...d, name: e.target.value }))}
            onKeyDown={(e) => e.key === "Enter" && submitFamily()}
            placeholder="Örn. Çayset"
            className="w-full border border-input rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-200"
          />
        </Modal.Body>
        <Modal.Footer>
          <button
            onClick={() => setFamilyDraft(null)}
            className="px-3.5 py-2 text-sm font-semibold text-gray-600 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
          >
            Vazgeç
          </button>
          <button
            onClick={submitFamily}
            disabled={!(familyDraft?.name || "").trim()}
            className="px-3.5 py-2 text-sm font-semibold text-white bg-main rounded-lg hover:bg-mainSecond disabled:opacity-50"
          >
            {familyDraft?.family ? "Kaydet" : "Oluştur"}
          </button>
        </Modal.Footer>
      </Modal>

      <PartTypeModal
        open={editing?.kind === "part"}
        part={editing?.item}
        allParts={parts}
        machineTypes={machineTypes}
        families={families}
        onClose={() => setEditing(null)}
        onSubmit={submitPart}
        saving={saving}
      />
      <MachineTypeModal
        open={editing?.kind === "machine"}
        machineType={editing?.item}
        allParts={parts}
        onClose={() => setEditing(null)}
        onSubmit={submitMachine}
        saving={saving}
      />
    </PageShell>
  );
}
